import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  IconButton,
  Portal,
  Paper,
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import CloseIcon from '@mui/icons-material/Close';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import TouchAppIcon from '@mui/icons-material/TouchApp';

interface TutorialStep {
  title: string;
  content: string;
  targetElement: string | null;
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** If true, this step requires a reference unit to be set */
  requiresReference?: boolean;
}

// Step indices for reference: Comparable = 5, Budget = 6
const COMPARABLE_STEP = 5;
const BUDGET_STEP = 6;

const TUTORIAL_STEPS: TutorialStep[] = [
  {
    title: 'Welcome to Site Plan Detail!',
    content:
      'This page provides a comprehensive view of a new launch project. It displays the site plan, available units, comparable listings, and more — all in one place. Let\'s walk through each section!',
    targetElement: null,
  },
  {
    title: 'Project Information',
    content:
      'This collapsible section shows key project details.\n\n• Details tab: Project name, TOP date, tenure, total units, and developer info.\n• Amenities tab: Nearby MRT stations, schools, supermarkets, and preschools within a configurable radius.\n• Files tab: View the project factsheet and brochure PDFs.\n• Members tab: See team members assigned to this project.',
    targetElement: '[data-tutorial="project-information"]',
    position: 'bottom',
  },
  {
    title: 'Site Plan',
    content:
      'The interactive site plan image lets you explore the project layout.\n\n• Zoom in/out using pinch, scroll wheel, or double-click.\n• Pan around by clicking and dragging.\n• Toggle data point overlays to see unit availability on the plan.\n• Use the heat map toggle to visualize sold vs available units.\n• Filter by bedroom type or unit type to highlight specific stacks.',
    targetElement: '[data-tutorial="site-plan"]',
    position: 'bottom',
  },
  {
    title: 'Tower View',
    content:
      'The tower view shows a block-by-block grid of all units.\n\n• Each cell represents a unit — color-coded by availability (green = available, red = sold).\n• Click on a unit to view its details or set it as a reference.\n• Toggle "Bedroom color" to color-code units by bedroom count.\n• Switch between blocks using the block selector.\n• Hover over a unit to see quick price and area info.',
    targetElement: '[data-tutorial="tower-view"]',
    position: 'bottom',
  },
  {
    title: 'Available Units',
    content:
      'This table lists all available units in the project.\n\n• Sort by any column (price, PSF, area, floor, etc.).\n• Filter units by block, bedroom, floor range, and more.\n• Toggle column visibility with the "Columns" button.\n• Click on a unit row to view its floor plan or set as reference.\n• The "BEST PX BEDROOM TYPE" tab shows the best-priced unit for each bedroom type.',
    targetElement: '[data-tutorial="available-units"]',
    position: 'top',
  },
  {
    title: 'Comparable Units',
    content:
      'When a reference unit is set, this section shows comparable units from other projects.\n\n• Units are matched by similar area, bedroom count, and location.\n• Compare prices, PSF, and other attributes side-by-side.\n• Use the area tolerance slider to widen or narrow the match range.\n• Click the thumbs-up icon to rate a comparable unit.\n• View floor plan comparisons between the reference and comparable units.',
    targetElement: '[data-tutorial="comparable-units"]',
    position: 'top',
    requiresReference: true,
  },
  {
    title: 'Budget Matched Units (Cross Project)',
    content:
      'This section finds units across all projects that fit within a budget range based on your reference unit\'s price.\n\n• Adjust the budget tolerance slider to widen the price range.\n• Filter by region (CCR, OCR, RCR), tenure, district, and more.\n• Use exclusion filters to remove specific projects or categories.\n• Units are sorted by distance from the reference project by default.',
    targetElement: '[data-tutorial="budget-matched"]',
    position: 'top',
    requiresReference: true,
  },
  {
    title: 'Reference Unit (Right Panel)',
    content:
      'The right panel shows project information and the currently selected reference unit.\n\n• View project name, TOP date, district, region, and total units.\n• Access factsheet, brochure, and export report buttons.\n• The reference unit section shows block, floor, stack, bedroom, area, and price.\n• Clear or change the reference unit from here.\n• Collapse the panel to maximize the main content area.',
    targetElement: '[data-tutorial="right-panel"]',
    position: 'left',
  },
];

const STORAGE_KEY = 'sitePlanTutorialProgress';

// Duration each step is shown during auto-play (ms)
const AUTO_PLAY_DURATION = 6000;

interface SitePlanTutorialProps {
  open: boolean;
  onClose: () => void;
  hasReferenceUnit: boolean;
}

// Highlight overlay around the target element
const TutorialHighlight: React.FC<{ coords: { x: number; y: number; width: number; height: number } }> = ({ coords }) => (
  <Box
    sx={{
      position: 'fixed',
      left: `${coords.x - 4}px`,
      top: `${coords.y - 4}px`,
      width: `${coords.width + 8}px`,
      height: `${coords.height + 8}px`,
      border: '3px solid #e67e22',
      borderRadius: '8px',
      backgroundColor: 'rgba(230, 126, 34, 0.08)',
      pointerEvents: 'none',
      zIndex: 9998,
      animation: 'sitePlanTutorialPulse 2s ease-in-out infinite, sitePlanTutorialFadeIn 0.3s ease-in',
      '@keyframes sitePlanTutorialPulse': {
        '0%, 100%': { boxShadow: '0 0 0 0 rgba(230, 126, 34, 0.7)' },
        '50%': { boxShadow: '0 0 0 15px rgba(230, 126, 34, 0)' },
      },
      '@keyframes sitePlanTutorialFadeIn': {
        '0%': { opacity: 0 },
        '100%': { opacity: 1 },
      },
    }}
  />
);

// Floating description popup positioned relative to the highlighted element
const TutorialOverlayPopup: React.FC<{
  step: TutorialStep;
  stepIndex: number;
  totalSteps: number;
  coords: { x: number; y: number; width: number; height: number };
  onNext: () => void;
  onPrev: () => void;
  onStop: () => void;
  isAutoPlaying: boolean;
}> = ({ step, stepIndex, totalSteps, coords, onNext, onPrev, onStop, isAutoPlaying }) => {
  const position = step.position || 'bottom';
  const popupMaxWidth = 400;
  const popupRef = useRef<HTMLDivElement>(null);
  const [adjustedPos, setAdjustedPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  useEffect(() => {
    // Use requestAnimationFrame to ensure the popup is rendered and we can measure it
    const rafId = requestAnimationFrame(() => {
      let top = 0;
      let left = 0;
      const gap = 12;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const popupHeight = popupRef.current?.offsetHeight || 300;

      left = coords.x + coords.width / 2 - popupMaxWidth / 2;
      left = Math.max(12, Math.min(left, vw - popupMaxWidth - 12));

      if (position === 'bottom') {
        top = coords.y + coords.height + gap;
        // If popup would overflow bottom, flip to top
        if (top + popupHeight > vh - 12) {
          top = coords.y - gap - popupHeight;
        }
      } else if (position === 'top') {
        top = coords.y - gap - popupHeight;
        // If popup would overflow top, flip to bottom
        if (top < 12) {
          top = coords.y + coords.height + gap;
        }
      } else if (position === 'left' || position === 'right') {
        top = coords.y + coords.height / 2 - popupHeight / 2;
        if (position === 'left') {
          left = coords.x - popupMaxWidth - gap;
        } else {
          left = coords.x + coords.width + gap;
        }
        left = Math.max(12, Math.min(left, vw - popupMaxWidth - 12));
      }

      // Final clamp: ensure popup stays within viewport
      top = Math.max(12, Math.min(top, vh - popupHeight - 12));

      setAdjustedPos({ top, left });
    });
    return () => cancelAnimationFrame(rafId);
  }, [coords, position]);

  return (
    <Paper
      ref={popupRef}
      elevation={8}
      sx={{
        position: 'fixed',
        top: adjustedPos.top,
        left: adjustedPos.left,
        maxWidth: popupMaxWidth,
        width: popupMaxWidth,
        maxHeight: 'calc(100vh - 24px)',
        zIndex: 10000,
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        animation: 'sitePlanTutorialFadeIn 0.3s ease-in',
        '@keyframes sitePlanTutorialFadeIn': {
          '0%': { opacity: 0, transform: 'translateY(8px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: '#e67e22',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 26,
              height: 26,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.8rem',
              fontWeight: 700,
            }}
          >
            {stepIndex + 1}
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {step.title}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onStop} sx={{ color: '#fff', ml: 1 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ px: 2, py: 1.5, overflow: 'auto', flex: 1, minHeight: 0, bgcolor: 'var(--color-surface, #1e1e1e)' }}>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: 'var(--color-text-strong, #fff)', lineHeight: 1.6 }}>
          {step.content}
        </Typography>
      </Box>

      {/* Footer with nav controls */}
      <Box
        sx={{
          px: 2,
          py: 1,
          bgcolor: 'var(--color-surface, #1e1e1e)',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb, 255,255,255), 0.5)' }}>
          {stepIndex + 1} / {totalSteps}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {isAutoPlaying && (
            <Button size="small" onClick={onStop} startIcon={<StopIcon sx={{ fontSize: 16 }} />} sx={{ mr: 0.5, minWidth: 0, color: 'var(--color-text-strong, #fff)' }}>
              Stop
            </Button>
          )}
          <IconButton size="small" onClick={onPrev} disabled={stepIndex === 0} sx={{ color: 'var(--color-text-strong, #fff)' }}>
            <NavigateBeforeIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={onNext}
            sx={{ color: 'var(--color-text-strong, #fff)' }}
          >
            <NavigateNextIcon />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
};

const SitePlanTutorial: React.FC<SitePlanTutorialProps> = ({ open, onClose, hasReferenceUnit }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [progress, setProgress] = useState<{ lastStep: number; completed: boolean; started: boolean }>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch { /* ignore */ }
    return { lastStep: 0, completed: false, started: false };
  });

  // "playing" = overlay mode (both auto-play and manual step-through on the page)
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const [playStep, setPlayStep] = useState(0);
  const [targetCoords, setTargetCoords] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const autoPlayTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Temporary highlight shown from dialog "Show me this section" button
  const [dialogHighlightCoords, setDialogHighlightCoords] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const dialogHighlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Dialog slides right to reveal the section, then slides back
  const [dialogSlid, setDialogSlid] = useState(false);

  // "Show me how" guided flow — walks user through setting a reference unit
  const [showMeHowActive, setShowMeHowActive] = useState(false);
  const [showMeHowStep, setShowMeHowStep] = useState<'scroll' | 'highlight-row' | 'click-row' | 'highlight-menu' | null>(null);
  const showMeHowTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Blinking highlight coords for the guided flow
  const [guideHighlightCoords, setGuideHighlightCoords] = useState<{ x: number; y: number; width: number; height: number } | null>(null);

  // "Waiting for reference" state — tutorial is paused, user must set a reference unit
  const [waitingForReference, setWaitingForReference] = useState(false);
  // The step to resume to once reference is set
  const [pendingResumeStep, setPendingResumeStep] = useState<number | null>(null);
  // Was auto-playing before the pause?
  const [wasAutoPlaying, setWasAutoPlaying] = useState(false);

  const saveProgress = useCallback((step: number, isCompleted = false) => {
    const newProgress = { lastStep: step, completed: isCompleted, started: step > 0 || isCompleted };
    setProgress(newProgress);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newProgress));
    } catch { /* ignore */ }
  }, []);

  // Scroll to and measure the target element
  const scrollAndMeasure = useCallback((selector: string | null) => {
    if (!selector) {
      setTargetCoords(null);
      return;
    }
    const element = document.querySelector(selector);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        const rect = element.getBoundingClientRect();
        setTargetCoords({ x: rect.left, y: rect.top, width: rect.width, height: rect.height });
      }, 500);
    }
  }, []);

  // Keep coords updated during scroll/resize while playing
  useEffect(() => {
    if (!isPlaying) return;
    const step = TUTORIAL_STEPS[playStep];
    if (!step?.targetElement) return;

    const update = () => {
      const el = document.querySelector(step.targetElement!);
      if (el) {
        const rect = el.getBoundingClientRect();
        setTargetCoords({ x: rect.left, y: rect.top, width: rect.width, height: rect.height });
      }
    };

    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isPlaying, playStep]);

  // Check if a step needs reference and we don't have one
  const stepNeedsReference = useCallback((stepIndex: number) => {
    return TUTORIAL_STEPS[stepIndex]?.requiresReference && !hasReferenceUnit;
  }, [hasReferenceUnit]);

  // Start play mode from a given step
  const startPlay = useCallback((fromStep: number, auto: boolean) => {
    setIsPlaying(true);
    setIsAutoPlaying(auto);
    setWaitingForReference(false);
    setPendingResumeStep(null);
    // Skip welcome step (no target) - go to first step with a target
    const startStep = TUTORIAL_STEPS[fromStep]?.targetElement ? fromStep : fromStep + 1;
    const step = TUTORIAL_STEPS[startStep];
    setPlayStep(startStep);
    saveProgress(startStep);
    if (step?.targetElement) {
      scrollAndMeasure(step.targetElement);
    }
  }, [saveProgress, scrollAndMeasure]);

  const stopPlay = useCallback(() => {
    setIsPlaying(false);
    setIsAutoPlaying(false);
    setWaitingForReference(false);
    setPendingResumeStep(null);
    setTargetCoords(null);
    if (autoPlayTimer.current) {
      clearTimeout(autoPlayTimer.current);
      autoPlayTimer.current = null;
    }
  }, []);

  // Enter "waiting for reference" mode: pause play, highlight Available Units, show prompt
  const enterWaitingForReference = useCallback((resumeStep: number, auto: boolean) => {
    // Pause auto-play timer
    if (autoPlayTimer.current) {
      clearTimeout(autoPlayTimer.current);
      autoPlayTimer.current = null;
    }
    setWaitingForReference(true);
    setPendingResumeStep(resumeStep);
    setWasAutoPlaying(auto);
    // Highlight the Available Units section so user knows where to click
    scrollAndMeasure('[data-tutorial="available-units"]');
  }, [scrollAndMeasure]);

  const playNext = useCallback(() => {
    if (playStep < TUTORIAL_STEPS.length - 1) {
      let nextStep = playStep + 1;
      // Skip steps without targets
      while (nextStep < TUTORIAL_STEPS.length && !TUTORIAL_STEPS[nextStep]?.targetElement) {
        nextStep++;
      }
      if (nextStep >= TUTORIAL_STEPS.length) {
        saveProgress(TUTORIAL_STEPS.length, true);
        stopPlay();
        return;
      }
      // Check if the next step requires a reference unit
      if (stepNeedsReference(nextStep)) {
        enterWaitingForReference(nextStep, isAutoPlaying);
        return;
      }
      setPlayStep(nextStep);
      saveProgress(nextStep);
      scrollAndMeasure(TUTORIAL_STEPS[nextStep].targetElement);
    } else {
      saveProgress(TUTORIAL_STEPS.length, true);
      stopPlay();
    }
  }, [playStep, saveProgress, scrollAndMeasure, stopPlay, stepNeedsReference, enterWaitingForReference, isAutoPlaying]);

  const playPrev = useCallback(() => {
    if (playStep > 0) {
      let prevStep = playStep - 1;
      while (prevStep >= 0 && !TUTORIAL_STEPS[prevStep]?.targetElement) {
        prevStep--;
      }
      if (prevStep < 0) return;
      setPlayStep(prevStep);
      saveProgress(prevStep);
      setWaitingForReference(false);
      setPendingResumeStep(null);
      scrollAndMeasure(TUTORIAL_STEPS[prevStep].targetElement);
    }
  }, [playStep, saveProgress, scrollAndMeasure]);

  // When hasReferenceUnit becomes true while waiting, auto-resume
  useEffect(() => {
    if (waitingForReference && hasReferenceUnit && pendingResumeStep !== null) {
      setWaitingForReference(false);
      // Small delay so the section renders in the DOM
      setTimeout(() => {
        setPlayStep(pendingResumeStep);
        saveProgress(pendingResumeStep);
        setIsAutoPlaying(wasAutoPlaying);
        scrollAndMeasure(TUTORIAL_STEPS[pendingResumeStep].targetElement);
        setPendingResumeStep(null);
      }, 800);
    }
  }, [hasReferenceUnit, waitingForReference, pendingResumeStep, wasAutoPlaying, saveProgress, scrollAndMeasure]);

  // Auto-play timer
  useEffect(() => {
    if (!isPlaying || !isAutoPlaying || waitingForReference) return;

    autoPlayTimer.current = setTimeout(() => {
      playNext();
    }, AUTO_PLAY_DURATION);

    return () => {
      if (autoPlayTimer.current) clearTimeout(autoPlayTimer.current);
    };
  }, [isPlaying, isAutoPlaying, playStep, playNext, waitingForReference]);

  // Dialog step navigation
  const handleNext = () => {
    if (activeStep < TUTORIAL_STEPS.length - 1) {
      const newStep = activeStep + 1;
      setActiveStep(newStep);
      saveProgress(newStep);
    } else {
      saveProgress(TUTORIAL_STEPS.length, true);
      onClose();
      setActiveStep(0);
    }
  };

  const handlePrevious = () => {
    if (activeStep > 0) {
      const newStep = activeStep - 1;
      setActiveStep(newStep);
      saveProgress(newStep);
    }
  };

  const handleRestart = () => {
    setActiveStep(0);
    stopPlay();
    saveProgress(0);
  };

  const showSectionFromDialog = useCallback((step: TutorialStep) => {
    if (!step.targetElement) return;
    const el = document.querySelector(step.targetElement);
    if (!el) return;

    // Clear any existing highlight timer
    if (dialogHighlightTimer.current) {
      clearTimeout(dialogHighlightTimer.current);
      setDialogHighlightCoords(null);
    }

    // Slide dialog out of the way
    setDialogSlid(true);

    // Scroll the section into view
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Measure after scroll + slide settles and show highlight
    setTimeout(() => {
      const rect = el.getBoundingClientRect();
      setDialogHighlightCoords({ x: rect.left, y: rect.top, width: rect.width, height: rect.height });

      // Keep coords updated while highlight is visible
      const updateCoords = () => {
        const r = el.getBoundingClientRect();
        setDialogHighlightCoords({ x: r.left, y: r.top, width: r.width, height: r.height });
      };
      window.addEventListener('scroll', updateCoords, true);

      // Fade dialog back in and hide highlight after 2 seconds
      dialogHighlightTimer.current = setTimeout(() => {
        setDialogSlid(false);
        setTimeout(() => {
          setDialogHighlightCoords(null);
          window.removeEventListener('scroll', updateCoords, true);
        }, 400);
      }, 2000);
    }, 600);
  }, []);

  // "Show me how" guided flow
  const stopShowMeHow = useCallback(() => {
    if (showMeHowTimer.current) {
      clearTimeout(showMeHowTimer.current);
      showMeHowTimer.current = null;
    }
    setShowMeHowActive(false);
    setShowMeHowStep(null);
    setGuideHighlightCoords(null);
    setDialogSlid(false);
  }, []);

  const startShowMeHow = useCallback(() => {
    setShowMeHowActive(true);
    setDialogSlid(true);
    setShowMeHowStep('scroll');

    const availableUnitsEl = document.querySelector('[data-tutorial="available-units"]');
    if (!availableUnitsEl) return;

    // Step 1: Scroll to available units
    availableUnitsEl.scrollIntoView({ behavior: 'smooth', block: 'center' });

    setTimeout(() => {
      // Step 2: Highlight the first row
      setShowMeHowStep('highlight-row');
      const firstRow = document.querySelector('[data-tutorial="unit-row-first"]') as HTMLElement;
      if (!firstRow) {
        setShowMeHowActive(false);
        setDialogSlid(false);
        return;
      }

      const rowRect = firstRow.getBoundingClientRect();
      setGuideHighlightCoords({ x: rowRect.left, y: rowRect.top, width: rowRect.width, height: rowRect.height });

      // Step 3: After blinking the row, simulate a click on it
      showMeHowTimer.current = setTimeout(() => {
        setShowMeHowStep('click-row');
        setGuideHighlightCoords(null);

        // Programmatically click the first row to open the context menu
        const clickEvent = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          clientX: rowRect.left + rowRect.width / 2,
          clientY: rowRect.top + rowRect.height / 2,
        });
        firstRow.dispatchEvent(clickEvent);

        // Step 4: Wait for menu to appear, then highlight "Set as Reference"
        showMeHowTimer.current = setTimeout(() => {
          setShowMeHowStep('highlight-menu');
          const menuItem = document.querySelector('[data-tutorial="set-reference-menu-item"]') as HTMLElement;
          if (menuItem) {
            const menuRect = menuItem.getBoundingClientRect();
            setGuideHighlightCoords({ x: menuRect.left, y: menuRect.top, width: menuRect.width, height: menuRect.height });
          }

          // Stay blinking until user clicks "Set as Reference" (hasReferenceUnit triggers cleanup)
        }, 400);
      }, 2000);
    }, 800);
  }, []);

  // Skip waiting and jump to the next non-reference step (right panel)
  const skipReferenceSteps = useCallback(() => {
    setWaitingForReference(false);
    setPendingResumeStep(null);
    // Jump to the right panel step (last step, index 7)
    const rightPanelStep = TUTORIAL_STEPS.length - 1;
    setPlayStep(rightPanelStep);
    saveProgress(rightPanelStep);
    setIsAutoPlaying(wasAutoPlaying);
    scrollAndMeasure(TUTORIAL_STEPS[rightPanelStep].targetElement);
  }, [saveProgress, scrollAndMeasure, wasAutoPlaying]);

  // Restore progress when dialog opens; clean up on close
  useEffect(() => {
    if (open) {
      const restored = progress.lastStep < TUTORIAL_STEPS.length ? progress.lastStep : 0;
      setActiveStep(restored);
    }
    return () => {
      stopPlay();
      stopShowMeHow();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // If reference gets set while showMeHow is active, clean up the guide
  useEffect(() => {
    if (hasReferenceUnit && showMeHowActive) {
      stopShowMeHow();
    }
  }, [hasReferenceUnit, showMeHowActive, stopShowMeHow]);

  const currentPlayStep = TUTORIAL_STEPS[playStep];

  return (
    <>
      {/* Dialog mode - only show when NOT playing */}
      {!isPlaying && (
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth="md"
          fullWidth
          slotProps={{
            backdrop: {
              sx: {
                transition: 'opacity 0.4s ease',
                ...(dialogSlid ? { opacity: '0.05 !important' } : {}),
              },
            },
          }}
          PaperProps={{
            sx: {
              minHeight: '400px',
              maxHeight: '80vh',
              transition: 'opacity 0.4s ease',
              ...(dialogSlid ? { opacity: 0.1 } : {}),
            },
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
            <Typography variant="h5" component="div">
              Site Plan Tutorial
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<PlayArrowIcon />}
                onClick={() => startPlay(1, true)}
                sx={{ bgcolor: '#e67e22', '&:hover': { bgcolor: '#d35400' } }}
              >
                Play
              </Button>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Stepper activeStep={activeStep} orientation="vertical">
              {TUTORIAL_STEPS.map((step, index) => (
                <Step key={index}>
                  <StepLabel
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      setActiveStep(index);
                      saveProgress(index);
                      showSectionFromDialog(step);
                    }}
                  >
                    <Typography variant="h6">{step.title}</Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line', mb: 2 }}>
                      {step.content}
                    </Typography>
                    {step.requiresReference && !hasReferenceUnit && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="body2" sx={{ color: 'warning.main', mb: 1 }}>
                          Note: This section is only visible after setting a reference unit. Click on any unit in the Available Units table and select "Set as Reference" to enable this section.
                        </Typography>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={startShowMeHow}
                          disabled={showMeHowActive}
                          sx={{ bgcolor: '#e67e22', '&:hover': { bgcolor: '#d35400' } }}
                        >
                          {showMeHowActive ? 'Showing...' : 'Show me how'}
                        </Button>
                      </Box>
                    )}
                    {step.targetElement && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => showSectionFromDialog(step)}
                        disabled={step.requiresReference && !hasReferenceUnit}
                        sx={{ mb: 1 }}
                      >
                        Show me this section
                      </Button>
                    )}
                  </StepContent>
                </Step>
              ))}
            </Stepper>
            {activeStep === TUTORIAL_STEPS.length && (
              <Box sx={{ mt: 3, p: 2, backgroundColor: 'success.light', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Congratulations!
                </Typography>
                <Typography variant="body1" paragraph>
                  You've completed the Site Plan tutorial! You're now ready to explore projects like a pro.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => {
                    saveProgress(TUTORIAL_STEPS.length, true);
                    onClose();
                    setActiveStep(0);
                  }}
                  sx={{ mt: 1 }}
                >
                  Get Started
                </Button>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 2, pb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                Step {activeStep + 1} of {TUTORIAL_STEPS.length}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button onClick={handleRestart} startIcon={<ReplayIcon />} size="small" color="secondary" sx={{ flexShrink: 0 }}>
                  Restart
                </Button>
                <Button onClick={handlePrevious} disabled={activeStep === 0} size="small" sx={{ flexShrink: 0 }}>
                  Previous
                </Button>
                <Button onClick={handleNext} variant="contained" size="small" sx={{ flexShrink: 0 }}>
                  {activeStep < TUTORIAL_STEPS.length - 1 ? 'Next' : 'Finish'}
                </Button>
              </Box>
            </Box>
          </DialogActions>
        </Dialog>
      )}

      {/* Play mode overlay - highlight + description popup on the page */}
      {isPlaying && !waitingForReference && currentPlayStep && (
        <Portal>
          {/* Semi-transparent backdrop */}
          <Box
            onClick={stopPlay}
            sx={{
              position: 'fixed',
              inset: 0,
              bgcolor: 'rgba(0,0,0,0.35)',
              zIndex: 9997,
              animation: 'sitePlanTutorialFadeIn 0.3s ease-in',
              '@keyframes sitePlanTutorialFadeIn': {
                '0%': { opacity: 0 },
                '100%': { opacity: 1 },
              },
            }}
          />

          {/* Highlight border around the target */}
          {targetCoords && (
            <>
              <TutorialHighlight coords={targetCoords} />
              {/* Cut out the highlighted area from the backdrop so it's visible */}
              <Box
                sx={{
                  position: 'fixed',
                  left: `${targetCoords.x - 4}px`,
                  top: `${targetCoords.y - 4}px`,
                  width: `${targetCoords.width + 8}px`,
                  height: `${targetCoords.height + 8}px`,
                  borderRadius: '8px',
                  bgcolor: 'transparent',
                  boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)',
                  zIndex: 9997,
                  pointerEvents: 'none',
                }}
              />
              {/* Floating description popup */}
              <TutorialOverlayPopup
                step={currentPlayStep}
                stepIndex={playStep}
                totalSteps={TUTORIAL_STEPS.length}
                coords={targetCoords}
                onNext={playNext}
                onPrev={playPrev}
                onStop={stopPlay}
                isAutoPlaying={isAutoPlaying}
              />
            </>
          )}

          {/* If no target (e.g. welcome step), show centered popup */}
          {!currentPlayStep.targetElement && (
            <Paper
              elevation={8}
              sx={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                maxWidth: 450,
                width: '90vw',
                zIndex: 10000,
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#e67e22', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{currentPlayStep.title}</Typography>
                <IconButton size="small" onClick={stopPlay} sx={{ color: '#fff' }}><CloseIcon fontSize="small" /></IconButton>
              </Box>
              <Box sx={{ px: 2.5, py: 2, bgcolor: 'var(--color-surface, #1e1e1e)' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: 'var(--color-text-strong, #fff)', lineHeight: 1.6 }}>
                  {currentPlayStep.content}
                </Typography>
              </Box>
              <Box sx={{ px: 2.5, py: 1, bgcolor: 'var(--color-surface, #1e1e1e)', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'flex-end' }}>
                <Button size="small" variant="contained" onClick={playNext} sx={{ bgcolor: '#e67e22', '&:hover': { bgcolor: '#d35400' } }}>
                  Next
                </Button>
              </Box>
            </Paper>
          )}
        </Portal>
      )}

      {/* "Waiting for reference" overlay — guides user to set a reference unit */}
      {isPlaying && waitingForReference && (
        <Portal>
          {/* Lighter backdrop that allows interaction */}
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              bgcolor: 'rgba(0,0,0,0.2)',
              zIndex: 9996,
              pointerEvents: 'none',
            }}
          />

          {/* Highlight the Available Units table */}
          {targetCoords && (
            <>
              <TutorialHighlight coords={targetCoords} />
              <Box
                sx={{
                  position: 'fixed',
                  left: `${targetCoords.x - 4}px`,
                  top: `${targetCoords.y - 4}px`,
                  width: `${targetCoords.width + 8}px`,
                  height: `${targetCoords.height + 8}px`,
                  borderRadius: '8px',
                  bgcolor: 'transparent',
                  boxShadow: '0 0 0 9999px rgba(0,0,0,0.2)',
                  zIndex: 9996,
                  pointerEvents: 'none',
                }}
              />
            </>
          )}

          {/* Instruction popup - fixed at top center */}
          <Paper
            elevation={12}
            sx={{
              position: 'fixed',
              top: 80,
              left: '50%',
              transform: 'translateX(-50%)',
              maxWidth: 480,
              width: '90vw',
              zIndex: 10000,
              borderRadius: 2,
              overflow: 'hidden',
              animation: 'sitePlanTutorialFadeIn 0.3s ease-in',
              '@keyframes sitePlanTutorialFadeIn': {
                '0%': { opacity: 0, transform: 'translateX(-50%) translateY(-12px)' },
                '100%': { opacity: 1, transform: 'translateX(-50%) translateY(0)' },
              },
            }}
          >
            {/* Header */}
            <Box
              sx={{
                px: 2.5,
                py: 1.5,
                bgcolor: '#e67e22',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <TouchAppIcon sx={{ fontSize: 28 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Set a Reference Unit
              </Typography>
            </Box>

            {/* Body */}
            <Box sx={{ px: 2.5, py: 2, bgcolor: 'var(--color-surface, #1e1e1e)' }}>
              <Typography variant="body2" sx={{ color: 'var(--color-text-strong, #fff)', lineHeight: 1.7, mb: 1.5 }}>
                To continue the tutorial, you need to set a reference unit first.
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--color-text-strong, #fff)', lineHeight: 1.7, mb: 1.5 }}>
                The <strong>Comparable Units</strong> and <strong>Budget Matched</strong> sections only appear when a reference unit is selected. They compare other units against your chosen reference to help you find the best deal.
              </Typography>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'rgba(230, 126, 34, 0.12)',
                  border: '1px solid rgba(230, 126, 34, 0.3)',
                }}
              >
                <Typography variant="body2" sx={{ color: 'var(--color-text-strong, #fff)', fontWeight: 600, mb: 0.5 }}>
                  How to set a reference:
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--color-text-strong, #fff)', lineHeight: 1.7 }}>
                  1. Click on any unit row in the <strong>Available Units</strong> table below.{'\n'}
                  2. Select <strong>"Set as Reference Unit"</strong> from the menu.{'\n'}
                  3. The tutorial will automatically continue.
                </Typography>
              </Box>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                px: 2.5,
                py: 1.25,
                bgcolor: 'var(--color-surface, #1e1e1e)',
                borderTop: '1px solid rgba(255,255,255,0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Button
                size="small"
                onClick={skipReferenceSteps}
                sx={{ color: 'rgba(var(--color-text-rgb, 255,255,255), 0.6)' }}
              >
                Skip these sections
              </Button>
              <Button
                size="small"
                onClick={stopPlay}
                variant="outlined"
                sx={{ borderColor: 'rgba(var(--color-text-rgb, 255,255,255), 0.3)', color: 'var(--color-text-strong, #fff)' }}
              >
                Exit Tutorial
              </Button>
            </Box>
          </Paper>
        </Portal>
      )}

      {/* Temporary highlight from dialog "Show me this section" button */}
      {dialogHighlightCoords && !isPlaying && (
        <Portal>
          <TutorialHighlight coords={dialogHighlightCoords} />
        </Portal>
      )}

      {/* "Show me how" guided flow highlight — blinks to draw attention */}
      {showMeHowActive && guideHighlightCoords && (
        <Portal>
          <Box
            sx={{
              position: 'fixed',
              left: `${guideHighlightCoords.x - 4}px`,
              top: `${guideHighlightCoords.y - 4}px`,
              width: `${guideHighlightCoords.width + 8}px`,
              height: `${guideHighlightCoords.height + 8}px`,
              border: '3px solid #e67e22',
              borderRadius: '8px',
              backgroundColor: 'rgba(230, 126, 34, 0.15)',
              pointerEvents: 'none',
              zIndex: 99999,
              animation: 'sitePlanGuideBlink 0.6s ease-in-out infinite',
              '@keyframes sitePlanGuideBlink': {
                '0%, 100%': {
                  boxShadow: '0 0 8px 2px rgba(230, 126, 34, 0.8)',
                  borderColor: '#e67e22',
                },
                '50%': {
                  boxShadow: '0 0 20px 6px rgba(230, 126, 34, 0.4)',
                  borderColor: '#f39c12',
                },
              },
            }}
          />
          {/* Label showing what to do */}
          {showMeHowStep === 'highlight-row' && (
            <Paper
              elevation={6}
              sx={{
                position: 'fixed',
                left: `${guideHighlightCoords.x + guideHighlightCoords.width / 2 - 100}px`,
                top: `${guideHighlightCoords.y - 44}px`,
                px: 2,
                py: 0.75,
                bgcolor: '#e67e22',
                color: '#fff',
                borderRadius: 1,
                zIndex: 99999,
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Click on any unit row
              </Typography>
            </Paper>
          )}
          {showMeHowStep === 'highlight-menu' && (
            <Paper
              elevation={6}
              sx={{
                position: 'fixed',
                left: `${guideHighlightCoords.x + guideHighlightCoords.width + 8}px`,
                top: `${guideHighlightCoords.y + guideHighlightCoords.height / 2 - 16}px`,
                px: 2,
                py: 0.75,
                bgcolor: '#e67e22',
                color: '#fff',
                borderRadius: 1,
                zIndex: 99999,
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Click here to set reference
              </Typography>
            </Paper>
          )}
        </Portal>
      )}
    </>
  );
};

export { SitePlanTutorial, STORAGE_KEY as SITE_PLAN_TUTORIAL_STORAGE_KEY };
export default SitePlanTutorial;
